import { useLayoutEffect, useState } from "react";

/**
 * A textarea's ref that keeps the box as tall as its text: on every change of
 * text, whenever its container's width changes (a phone turned, a card laid
 * out after the font loads), and when the box first appears — a callback ref,
 * because a box shown later with the same text (the translation instruction,
 * once a language is chosen) would otherwise keep its default rows. By hand,
 * because `field-sizing: content` is not in every browser a phone has, and a
 * fixed box clipped the voice description to one line on the owner's phone
 * (2026-10-08). Every growing textarea in the studio uses this; four copies of
 * it drifted apart, one of them two pixels short of its border.
 */
export function useGrowToText(text: string) {
  const [el, setEl] = useState<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    if (!el) return;
    const fit = () => {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el.parentElement ?? el);
    return () => observer.disconnect();
  }, [el, text]);
  return setEl;
}
